import { QuestionCircleOutlined } from '@ant-design/icons';
import { ModalForm, ProFormSelect } from '@ant-design/pro-form';
import { Button, Collapse, message, Space, Checkbox, Switch, Tooltip } from 'antd';
import { FormInstance } from 'antd/es/form/hooks/useForm';
import classnames from 'classnames';
import { debounce, get, isEmpty } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import CloseCircleIcon from '@/assets/images/public/close-circle.svg';
import i18n from '@/i18n';
import { getPid, isFlutter } from '@/infrastructure/product-info';
import { Platform } from '@/infrastructure/utils/app';
import { getTips } from '@/presentation/components/issue-list/components/tips';
import { ModalIconType, ModalMsg, ModalType } from '@/presentation/components/modal-msg';
import { ConfigTabType, isConfigManage } from '@/presentation/pages/setting/app-deliver-config/api';
import { CustomizeData } from '@/presentation/pages/setting/app-deliver-config/components/customize-item';
import {
  renderModal,
  RenderSubConfigItem,
} from '@/presentation/pages/setting/app-deliver-config/components/sub-config-item';
import { OperationType } from '@/presentation/pages/setting/app-deliver-config/config-modal/edit';
import { CUSTOMIZE_CONFIG } from '@/presentation/pages/setting/app-deliver-config/constant';
import { SampleRatio } from '@/presentation/pages/setting/app-deliver-config/type';
import {
  isCustomize,
  transform2Form,
  transform2Value,
} from '@/presentation/pages/setting/app-deliver-config/utils';
import { useStores } from '@/presentation/store';
import { PropsWithStyles } from '@/proto/common';
import styles from './index.module.scss';

export type RecordType = Record<string, any>;

export interface FormContent {
  configs?: RecordType[];
  sampleRatio?: number;
}

export interface ConfigContent {
  v7: {
    sample_ratio?: number;
    crash?: RecordType;
    atta?: RecordType;
    features?: RecordType[];
  };
}

interface IProps {
  tab: ConfigTabType;
  type: OperationType;
  content: string;
  fullContent?: string;
  form?: FormInstance;
  readonly?: boolean;
  onChange?: (content: string) => void;
}

export const ConfigurationContent: FC<PropsWithStyles<IProps>> = observer((props) => {
  const {
    configs: operation = {},
    androidFullConfig = {},
    iosFullConfig = {},
    flutterFullConfig = {},
    windowsFullConfig = {},
    harmonyOSFullConfig = {},
    macFullConfig = {},
    appleTVFullConfig = {},
  } = useStores().ConfigStore;
  const { configMode = {}, sdkConfig } = operation;
  const fullConfigMode = {
    ...configMode.android,
    ...configMode.ios,
    ...configMode.flutter,
    ...configMode.windows,
  };
  const platformModule = {
    ...fullConfigMode,
    [CUSTOMIZE_CONFIG.name]: CUSTOMIZE_CONFIG.label,
  };
  const {
    sampleRatio: defaultSampleRatio,
    chunkConfig,
  } = transform2Form(
    props.content, androidFullConfig,
    iosFullConfig, flutterFullConfig,
    windowsFullConfig, harmonyOSFullConfig,
    macFullConfig, appleTVFullConfig, fullConfigMode,
  ) || {};
  const isEmptyConfig = (props.tab === ConfigTabType.PLATFORM) && props.type === OperationType.ADD;
  const [visible, setVisible] = useState<boolean>(false);
  const [configs, setConfigs] = useState(isEmptyConfig ? [] : chunkConfig);
  const [sampleRatio, setSampleRatio] = useState(defaultSampleRatio);
  const [activeKey, setActiveKey] = useState<Array<string>>([]);
  const [collapsible, setCollapsible] = useState(false);
  const fullConfig = get(JSON.parse(props.fullContent || '{}'), 'v7', {});
  const parseContent = get(transform2Value(props.content), 'v7', {});
  const {
    chunkConfig: fullChunkConfig,
  } = transform2Form(
    props.fullContent!, androidFullConfig, iosFullConfig, flutterFullConfig,
    windowsFullConfig, harmonyOSFullConfig,
    macFullConfig, appleTVFullConfig, fullConfigMode,
  ) || {};
  const addConfigRef = useRef<any>(null);

  useEffect(() => {
    props.onChange?.(JSON.stringify({
      configs,
      sampleRatio,
    }));
  }, [configs, sampleRatio]);

  useEffect(() => {
    setConfigs(isEmptyConfig ? [] : chunkConfig);
  }, [props.content]);


  const getConfigInfo = (name: string, platformConfig: RecordType) => {
    const config = get(platformConfig, name)
     || get(platformConfig, 'features')?.find((item: RecordType) => item.name === name)
     || chunkConfig.find(item => item?.[name])?.[name]
     || {};
    const options = Object.keys(config)?.map(item => ({ label: item, value: item }));

    return {
      options,
      config,
    };
  };

  const onFinish = async (values: RecordType) => {
    const { configItems = [] } = values || {};
    if (isEmpty(configItems)) {
      return;
    }
    const addConfigs: RecordType[] = [];
    const customizeCount = configs
      .filter(item => Object.keys(item)?.[0]?.indexOf?.(CUSTOMIZE_CONFIG.name) > -1).length;
    configItems.forEach((name: string) => {
      const config = get(fullConfig, name) || (get(fullConfig, 'features') || [])?.find((item: RecordType) => item.name === name) || {};
      addConfigs.push({ [name === CUSTOMIZE_CONFIG.name ? `${name}-${customizeCount + 1}` : name]: config });
    });
    setConfigs(configs => [...configs, ...addConfigs]);
    setVisible(false);
    return true;
  };

  // 添加所有
  const addAllConfig = () => {
    const all = getOptions()?.filter?.(item => !item.disabled && item.value !== CUSTOMIZE_CONFIG.name)
      ?.map?.(item => item.value);
    if (isEmpty(all)) {
      message.warn(i18n.t('已添加所有配置项'));
      return;
    }
    const addConfigs: RecordType[] = [];
    const customizeCount = configs
      .filter(item => Object.keys(item)?.[0]?.indexOf?.(CUSTOMIZE_CONFIG.name) > -1).length;
    all.forEach((name: string) => {
      const config = get(fullConfig, name) || (get(fullConfig, 'features') || [])?.find((item: RecordType) => item.name === name) || {};
      addConfigs.push({ [name === CUSTOMIZE_CONFIG.name ? `${name}-${customizeCount + 1}` : name]: config });
    });
    ModalMsg({
      type: ModalType.CONFIRM,
      typeIcon: ModalIconType.INFO,
      content: i18n.t('确认添加所有配置项吗？'),
      onOk: () => {
        setConfigs(configs => [...configs, ...addConfigs]);
      },
    });
  };

  // 删除所有
  const delAllConfig = () => {
    ModalMsg({
      type: ModalType.CONFIRM,
      typeIcon: ModalIconType.RED_WARNING,
      content: i18n.t('确认删除所有配置项吗？'),
      onOk: () => {
        setConfigs([]);
      },
    });
  };

  const memoActiveKey = useMemo(() => configs?.map((configItem: any, index: number) => {
    const name = Object.keys(configItem)?.[0];
    return `${name}_${index}`;
  }), [configs]);

  const handleToggle = debounce(() => {
    setCollapsible(collapsible => !collapsible);
    if (collapsible) {
      setActiveKey([]);
      return;
    }
    setActiveKey(memoActiveKey || []);
  }, 200);

  const getOptions = () => fullChunkConfig.map((item) => {
    const name = Object.keys(item)?.[0];
    const isAdd = configs.find((config) => {
      const key = Object.keys(config)?.[0];
      return key === name;
    });
    return { label: platformModule[name], value: name, disabled: !!isAdd };
  }).concat(isConfigManage(props.tab)
    ? [{ label: CUSTOMIZE_CONFIG.label, value: CUSTOMIZE_CONFIG.name, disabled: false }] : []);

  const addConfigModal = <ModalForm
    trigger={<Button ref={addConfigRef} onClick={(e) => {
      e.stopPropagation();
      setVisible(true);
    }} type={'primary'}>{i18n.t('添加新配置项')}</Button>}
    visible={visible}
    title={<div className={styles.modalTitle}>{i18n.t('添加新配置项')}</div>}
    width={450}
    onFinish={onFinish}
    onClick={e => e.stopPropagation()}
    modalProps={{
      bodyStyle: { padding: '20px 65px' },
      onCancel: () => setVisible(false),
      destroyOnClose: true,
    }}
  >
    <ProFormSelect
      name={'configItems'}
      label={i18n.t('配置项')}
      mode={'multiple'}
      options={getOptions()}
      fieldProps={{
        onClick: e => e.stopPropagation(),
      }}
      rules={[{
        required: true,
        message: i18n.t('请选择配置项'),
      }]}
    />
  </ModalForm>;

  const getSDKConfig = () => {
    const pid = getPid();
    switch (pid) {
      case `${Platform.Android}`:
        if (isFlutter()) {
          return sdkConfig?.flutter;
        }
        return sdkConfig?.android;
      case `${Platform.IOS}`:
        return sdkConfig?.ios;
      case `${Platform.Windows}`:
        return sdkConfig?.windows;
      case `${Platform.HarmonyOS}`:
        return sdkConfig?.harmony;
      case `${Platform.Mac}`:
        return sdkConfig?.mac;
      default:
        return {};
    }
  };

  const renderHeader = (
    name: string,
    idx: number,
    value: RecordType,
  ) => {
    const sdkNameConfig = getSDKConfig();
    return <Space className={styles.collapseHeader}>
    <div>
      <span>{(name || '').indexOf(CUSTOMIZE_CONFIG.name) === 0 ? value?.name || get(sdkNameConfig, CUSTOMIZE_CONFIG.name)?.name : get(sdkNameConfig, name)?.name || name}</span>
      {sdkNameConfig?.[name]?.tips ? <Tooltip
        title={<pre>{sdkNameConfig?.[name]?.tips}</pre>}
        overlayStyle={{ maxWidth: 1100 }}
        placement={'top'}
      >
        <QuestionCircleOutlined style={{ color: '#A3A3A9', fontSize: '14px', marginLeft: 4 }}/>
      </Tooltip> : null}
    </div>
    {!props.readonly ? <img
      src={CloseCircleIcon}
      alt={''}
      className={styles.delConfig}
      onClick={(e) => {
        e.stopPropagation();
        const onDelete = () => {
          const newConfigs = configs?.filter((_item, index: number) => index !== idx);
          setConfigs(newConfigs);
        };
        renderModal(i18n.t('删除 {{name}}', { name: get(platformModule, name) || name }), i18n.t('删除此配置项后，相关功能的数据上报可能会受影响，确认要删除吗？'), onDelete);
      }}
    /> : null}
  </Space>;
  };

  const renderSampleRatio = () => <Space align={'center'}>
    <span>{i18n.t('关闭所有监控项')}{getTips('', i18n.t('目前，我们已经支持关闭所有性能监控项。但是，我们仍在不断扩展其他功能，例如崩溃、ANR、错误和OOM监控等。'))}</span>
    <Switch disabled={props.readonly} checked={!sampleRatio} onChange={(checked) => {
      const value = checked ? 0 : 1;
      setSampleRatio(value);
    }}/>
  </Space>;

  return <div className={styles.configurationContent}>
    <div className={'flex justify-between mb-[16px]'}>
      {!props.readonly ? <Space size={[16, 8]}>
        {addConfigModal}
        <Button onClick={addAllConfig} type={'primary'}>{i18n.t('添加所有配置项')}</Button>
        <Button onClick={delAllConfig} type={'primary'} className={styles.closeButton}>{i18n.t('删除所有配置项')}</Button>

        {props.tab === ConfigTabType.BUSINESS
          ? <Checkbox onChange={handleToggle} defaultChecked={collapsible}>{i18n.t('展开所有配置项')}</Checkbox>
          : null}
          {
            defaultSampleRatio === SampleRatio.ON ? <Collapse.Panel
            header={renderSampleRatio()}
        key={'sampleRatio'}
        showArrow={false}
        className={styles.globalSwitch}
        collapsible={'disabled'}
      /> : null
          }
      </Space> : <div/>}

    </div>

    <Collapse
      className={classnames(props.className, styles.contentCollapse)}
      style={props.style}
      activeKey={activeKey}
      onChange={key => setActiveKey(key as string[])}
    >
      {/* 其他配置 */}
      {configs?.map((configItem: any, index: number) => {
        const name = Object.keys(configItem)?.[0];
        const value = configItem[name];
        const isCustom = isCustomize(
          { name }, androidFullConfig, iosFullConfig, flutterFullConfig,
          windowsFullConfig, harmonyOSFullConfig,
          macFullConfig, appleTVFullConfig, fullConfigMode,
        )
         || isConfigManage(props.tab);

        return <Collapse.Panel
          header={renderHeader(name, index, value)}
          key={`${name}_${index}`}
          className={styles.collapsePanel}
        >
          {isCustom ? <CustomizeData
              configs={configs}
              setConfigs={setConfigs}
              config={configItem || {}}
              form={props.form}
              readonly={props.readonly}
            />
            : <RenderSubConfigItem
                form={props.form}
                name={name}
                config={value}
                data={configs}
                readonly={props.readonly}
                setData={setConfigs}
                options={getConfigInfo(name, fullConfig)?.options ?? []}
                fullConfig={getConfigInfo(name, fullConfig)?.config ?? {}}
                dataSource={getConfigInfo(name, parseContent)?.config ?? {}}
              />}
        </Collapse.Panel>;
      })}
    </Collapse>
  </div>;
});
